/** PrioridadesInternasTab — CRUD del catálogo SEPARADO de prioridades de Gestión Interna.
 *  No tiene SLA propio: el SLA se define en la matriz Área x Prioridad (pestaña SLA Interno). */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const empty = { nombre: '', color: '#3B82F6', nivel: 1, activo: true, orden: 0, es_default: false };

export default function PrioridadesInternasTab({ api }) {
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async (signal) => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/prioridades-internas', { signal });
      setItems(data);
    } catch (e) { if (isCanceled(e)) return; toast.error('Error cargando prioridades internas'); }
    finally { setLoading(false); }
  };

  useAbortableLoad((signal) => load(signal), []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setDialogOpen(true); };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar la prioridad interna "${it.nombre}"?`)) return;
    try { await api.delete(`/tickets/prioridades-internas/${it.id}`); toast.success('Prioridad interna eliminada'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error eliminando'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre, color: form.color,
        nivel: parseInt(form.nivel, 10), orden: parseInt(form.orden || 0, 10),
        activo: !!form.activo, es_default: !!form.es_default,
      };
      if (editing) await api.put(`/tickets/prioridades-internas/${editing.id}`, payload);
      else await api.post('/tickets/prioridades-internas', payload);
      toast.success(editing ? 'Prioridad interna actualizada' : 'Prioridad interna creada');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
    finally { setSaving(false); }
  };

  const marcarDefault = async (it) => {
    try {
      await api.put(`/tickets/prioridades-internas/${it.id}`, {
        nombre: it.nombre, color: it.color, nivel: it.nivel,
        activo: it.activo, orden: it.orden || 0, es_default: true,
      });
      toast.success(`"${it.nombre}" es ahora la prioridad interna por defecto`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error actualizando'); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex flex-col gap-4" data-testid="prioridades-internas-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Catálogo de prioridades para derivaciones internas. La marcada como <strong>Predeterminada</strong> se asigna automáticamente al derivar. El SLA se define en la pestaña <strong>SLA Interno</strong>.</p>
        {isAdmin && (
          <Button size="sm" onClick={openCreate} data-testid="crear-prioridad-interna-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg" data-testid={`prioridad-interna-row-${it.id}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: it.color || '#94a3b8' }} />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-2">
                <span className="text-xs font-mono text-slate-400">N{it.nivel}</span>
                {it.nombre}
                {it.es_default && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid={`prioridad-interna-default-${it.id}`}>Predeterminada</Badge>
                )}
              </div>
            </div>
            <Badge variant={it.activo ? 'default' : 'secondary'}>{it.activo ? 'Activo' : 'Inactivo'}</Badge>
            {isAdmin && (
              <>
                {!it.es_default && it.activo && (
                  <Button size="sm" variant="outline" onClick={() => marcarDefault(it)} data-testid={`set-default-prioridad-interna-${it.id}`}>
                    <Star className="w-4 h-4 mr-1" /> Predeterminada
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-prioridad-interna-${it.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`del-prioridad-interna-${it.id}`}>
                  <Trash2 className="w-4 h-4 text-rose-500" />
                </Button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No hay prioridades internas. Crea la primera.</p>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nueva'} prioridad interna</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="prioridad-interna-nombre" placeholder="Ej: Alta, Media, Baja" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nivel (orden)</Label>
                <Input type="number" min={1} value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} data-testid="prioridad-interna-nivel" />
              </div>
              <div className="space-y-1">
                <Label>Color</Label>
                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 p-1" data-testid="prioridad-interna-color" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
              <Label>Activo</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.es_default} onCheckedChange={(v) => setForm({ ...form, es_default: v })} data-testid="switch-prioridad-interna-default" />
              <Label>Prioridad por defecto al derivar</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="guardar-prioridad-interna-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{editing ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
